import React, { useState, useEffect } from 'react';
import { ROLE_LABELS } from '../../utils/permissions';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import TrialAccessModal from '../../components/Admin/TrialAccessModal';
import EditProfileModal from '../../components/Common/EditProfileModal';
import ChangePasswordModal from '../../components/Common/ChangePasswordModal';
import { getAdminSessions, revokeAdminSession } from '../../services/adminService';
import { getNotifyPrefs, setNotifyPrefs } from '../../services/notificationsService';
import useTelegramLink from '../../hooks/useTelegramLink';
import { confirmDialog } from '../../components/Common/confirm';
import toast from 'react-hot-toast';

const getInitials = (name) => {
  if (!name) return 'А';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const fmtWhen = (d) => {
  if (!d) return '—';
  const t = new Date(d);
  if (isNaN(t)) return '—';
  const mins = Math.floor((Date.now() - t.getTime()) / 60000);
  if (mins < 2) return 'сейчас';
  if (mins < 60) return `${mins} мин назад`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} ч назад`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} дн назад`;
  return fmtDate(d);
};

/* ── Иконки ─────────────────────────────────────────────────────────────── */
const Ic = ({ d, size = 18, sw = 1.8, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    {children || <path d={d} />}
  </svg>
);
const IcShield = ({ size = 15 }) => <Ic size={size} sw={2} d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />;
const IcBook = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);
const IcUsers = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IcInbox = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);
const IcStaff = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="4" width="16" height="17" rx="2.5" /><rect x="9" y="2.5" width="6" height="3" rx="1.2" /><circle cx="12" cy="11" r="2.3" /><path d="M8.2 17.4c0-2.1 1.7-3.5 3.8-3.5s3.8 1.4 3.8 3.5" />
  </svg>
);
const IcDesktop = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);
const IcMobile = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="2" width="12" height="20" rx="2.5" /><line x1="10.5" y1="18.5" x2="13.5" y2="18.5" />
  </svg>
);

/* Тумблер как в дизайне */
const Switch = ({ on, onChange, disabled }) => (
  <button type="button" disabled={disabled} onClick={() => onChange(!on)} className={`ap-sw${on ? ' on' : ''}`}><span /></button>
);

// Самолётик Telegram — тот же, что в карточке «Куратор в Telegram»
const IcLock = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10.5" width="16" height="10.5" rx="2.4"/><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"/></svg>);
const IcTgPlane = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M22 3 2 10.5l6 2.5 2.5 6L14 15l6 6z"/><path d="m8 13 6-4"/></svg>);

const NOTIFS = [
  { key: 'review',   t: 'Новое ДЗ на проверку', s: 'сразу, как ученик отправил работу' },
  { key: 'students', t: 'Новые ученики',        s: 'когда человек зарегистрировался сам' },
  { key: 'digest',   t: 'Сводка за неделю',     s: 'каждое воскресенье в 10:00 по МСК, с файлом' },
];

const AdminProfilePage = () => {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [trialOpen, setTrialOpen] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [soon, setSoon] = useState([]);      // тумблеры, под которые логики ещё нет
  const [tgInit, setTgInit] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    getNotifyPrefs()
      .then(r => {
        setPrefs(r.data);
        setSoon(r.soon || []);
        setTgInit(r.telegram || { available: false, connected: false, username: null });
      })
      .catch(() => setPrefs({}));
    loadSessions();
  }, []);

  const { tg, ready: tgReady, busy: tgBusy, waiting: tgWaiting, toggle: toggleTg } = useTelegramLink(tgInit);

  const loadSessions = () => getAdminSessions().then(r => setSessions(r.data || [])).catch(() => {});

  const toggleNotif = async (key, value) => {
    const prev = prefs;
    setPrefs(p => ({ ...p, [key]: value }));   // оптимистично, чтобы тумблер не «залипал»
    setSavingKey(key);
    try { await setNotifyPrefs({ [key]: value }); }
    catch { setPrefs(prev); toast.error('Не удалось сохранить'); }
    finally { setSavingKey(null); }
  };

  const endSession = async (s) => {
    if (!(await confirmDialog({ title: 'Завершить сессию?', message: `Вход с «${s.title}» будет закрыт — на этом устройстве придётся войти заново.`, confirmText: 'Завершить' }))) return;
    try { await revokeAdminSession(s.id); toast.success('Сессия завершена'); loadSessions(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Не удалось завершить'); }
  };

  const rows = [
    ['Имя', user?.name || '—'],
    ['Email', user?.email || '—'],
    ['Роль', ROLE_LABELS[user?.role] || 'Сотрудник'],
  ];

  // Каждое действие показываем только тому, кто его может выполнить.
  // У куратора не остаётся ни одного — тогда карточку не показываем вовсе.
  const actions = [
    { icon: <IcBook />,  label: 'Создать курс',        perm: 'courses.edit',    go: () => navigate('/admin/dashboard', { state: { createCourse: true } }) },
    { icon: <IcUsers />, label: 'Пригласить ученика',  perm: 'students.invite', go: () => navigate('/admin/students', { state: { invite: true } }) },
    { icon: <IcInbox />, label: 'Проверить ДЗ',        perm: 'homework.review', go: () => navigate('/admin/review') },
    { icon: <IcStaff />, label: 'Добавить сотрудника', perm: 'staff.manage',    go: () => navigate('/admin/staff', { state: { invite: true } }) },
  ].filter(a => can(a.perm));
  /* Карточка действий разная по ролям:
     главный админ — сетка кнопок, менеджер — одна большая «Пригласить ученика»,
     куратор — ничего, он только смотрит и проверяет ДЗ. */
  // менеджеру проверка ДЗ недоступна — тумблер, который ничего не пришлёт, не показываем
  const notifs = NOTIFS.filter(n => n.key !== 'review' || can('homework.review'));

  const quickMode = can('ui.quickActions') && actions.length > 0 ? 'full'
    : can('students.invite') ? 'single' : 'none';
  const showSessions = can('ui.sessions');
  // Доступ пробникам — правка курса, прячем по тому же праву
  const showTrial = can('courses.edit');
  // У куратора правая колонка пустеет — тогда данные и уведомления встают в ряд
  const twoUp = quickMode === 'none' && !showSessions;

  return (
    <div>
      <div className="s-page-header">
        <div>
          <h1 className="s-page-title">Профиль</h1>
          <p className="s-page-sub">Личные данные, доступ и настройки работы</p>
        </div>
      </div>

      <div className={`ap-grid${twoUp ? ' two-up' : ''}`}>
        {/* ── Левая колонка ── */}
        <div className="ap-col">
          <div className="ap-card">
            <div className="ap-hero">
              <div className="ap-ava">
                {user?.avatar
                  ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                  : getInitials(user?.name)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="ap-name">{user?.name || 'Администратор'}</div>
                <div className="ap-badges">
                  <span className="ap-role"><IcShield />{ROLE_LABELS[user?.role] || 'Сотрудник'}</span>
                  <span className="ap-since">В команде с {fmtDate(user?.created_at)}</span>
                </div>
              </div>
            </div>
            <div className="ap-rows">
              {rows.map(([k, v]) => (
                <div key={k} className="ap-row"><span>{k}</span><b>{v}</b></div>
              ))}
            </div>

            <div className="prof-actions">
              <button className="pg-btn" onClick={() => setEditOpen(true)}>Редактировать профиль</button>
              <button className="pg-btn ghost" onClick={() => setPwOpen(true)}>Сменить пароль</button>
            </div>
          </div>

          {!twoUp && (
          <div className="ap-card grow">
            <div className="ap-cap">Уведомления</div>
            <div>
              {notifs.map((n, i) => (
                <React.Fragment key={n.key}>
                  <div className="ap-notif">
                    <div style={{ minWidth: 0 }}>
                      <div className="ap-notif-t">{n.t}{soon.includes(n.key) && <span className="notif-soon">скоро</span>}</div>
                      <div className="ap-notif-s">{n.s}</div>
                    </div>
                    {/* пока настройки не пришли — заглушка того же размера, иначе тумблер
                        успевает отрисоваться выключенным и потом «переезжает» сам собой */}
                    {prefs
                      ? <Switch on={!!prefs[n.key]} disabled={soon.includes(n.key) || savingKey === n.key} onChange={(v) => toggleNotif(n.key, v)} />
                      : <span className="ap-sw-ghost" />}
                  </div>

                  {/* Telegram — вторым в списке. Это не событие, а канал: всё,
                      что приходит в колокольчик, бот дублирует в личные сообщения. */}
                  {i === 0 && (
                    <div className={`ap-notif tg-row${tg.connected ? '' : ' off'}`}>
                      <div className="tg-left">
                        <span className="tg-ic"><IcTgPlane /></span>
                        <div style={{ minWidth: 0 }}>
                          <div className="ap-notif-t">Telegram</div>
                          <div className="ap-notif-s">
                            {tg.connected
                              ? <>подключён{tg.username ? <> · <span className="tg-user">@{tg.username}</span></> : ''} — уведомления приходят в бота</>
                              : tgWaiting
                                ? <span className="tg-wait"><i />ждём «Начать» в боте…</span>
                                : tg.available ? 'присылать уведомления в личные сообщения' : 'бот скоро появится'}
                          </div>
                        </div>
                      </div>
                      {tgReady
                        ? <Switch on={tg.connected} disabled={!tg.available || tgBusy} onChange={toggleTg} />
                        : <span className="ap-sw-ghost" />}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          )}
        </div>

        {/* ── Правая колонка ── */}
        <div className="ap-col">
          {quickMode === 'full' && (
            <div className="ap-card">
              <div className="ap-cap">Быстрые действия</div>
              <div className="ap-quick">
                {actions.map(a => (
                  <button key={a.label} className="ap-quick-btn" onClick={a.go}>
                    <span className="ic">{a.icon}</span>
                    <span className="tx">{a.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {quickMode === 'single' && (
            <button type="button" className="ap-card ap-big-action"
              onClick={() => navigate('/admin/students', { state: { invite: true } })}>
              <span className="ap-big-ic"><IcUsers /></span>
              <span className="ap-big-t">Пригласить ученика</span>
              <span className="ap-big-s">Добавить человека и сразу зачислить на курс</span>
            </button>
          )}

          {showSessions && (
          <div className="ap-card grow">
            <div className="ap-cap">Активные сессии</div>
            {sessions.length === 0 ? (
              <div className="ap-empty">Других входов нет</div>
            ) : (
              <div className="ap-sess-list">
                {sessions.map((s, i) => (
                  <div key={s.id} className="ap-sess" style={i === sessions.length - 1 ? { borderBottom: 'none' } : undefined}>
                    <div className={`ap-sess-ic${s.current ? ' on' : ''}`}>{s.mobile ? <IcMobile /> : <IcDesktop />}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="ap-sess-t">{s.title}</div>
                      <div className="ap-sess-s">{s.current ? 'это устройство' : `был(а) ${fmtWhen(s.lastSeenAt)}`}{s.ip ? ` · ${s.ip}` : ''}</div>
                    </div>
                    {s.current
                      ? <span className="ap-now">Сейчас</span>
                      : <button className="ap-end" onClick={() => endSession(s)}>Завершить</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {showTrial && (
            <button type="button" className="ap-card ap-big-action" onClick={() => setTrialOpen(true)}>
              <span className="ap-big-ic"><IcLock /></span>
              <span className="ap-big-t">Доступ к пробным занятиям</span>
              <span className="ap-big-s">Отметить, что открыто ученикам с пометкой «пробник»</span>
            </button>
          )}

          {/* У куратора справа только уведомления — карточка переезжает сюда,
              чтобы обе стояли в один ряд, а не одна под другой */}
          {twoUp && (
            <div className="ap-card grow">
              <div className="ap-cap">Уведомления</div>
              <div>
                {notifs.map((n, i) => (
                  <React.Fragment key={n.key}>
                    <div className="ap-notif">
                      <div style={{ minWidth: 0 }}>
                        <div className="ap-notif-t">{n.t}{soon.includes(n.key) && <span className="notif-soon">скоро</span>}</div>
                        <div className="ap-notif-s">{n.s}</div>
                      </div>
                      {prefs
                        ? <Switch on={!!prefs[n.key]} disabled={soon.includes(n.key) || savingKey === n.key} onChange={(v) => toggleNotif(n.key, v)} />
                        : <span className="ap-sw-ghost" />}
                    </div>
                    {i === 0 && (
                      <div className={`ap-notif tg-row${tg.connected ? '' : ' off'}`}>
                        <div className="tg-left">
                          <span className="tg-ic"><IcTgPlane /></span>
                          <div style={{ minWidth: 0 }}>
                            <div className="ap-notif-t">Telegram</div>
                            <div className="ap-notif-s">
                              {tg.connected
                                ? <>подключён{tg.username ? <> · <span className="tg-user">@{tg.username}</span></> : ''} — уведомления приходят в бота</>
                                : tgWaiting
                                  ? <span className="tg-wait"><i />ждём «Начать» в боте…</span>
                                  : tg.available ? 'присылать уведомления в личные сообщения' : 'бот скоро появится'}
                            </div>
                          </div>
                        </div>
                        {tgReady
                          ? <Switch on={tg.connected} disabled={!tg.available || tgBusy} onChange={toggleTg} />
                          : <span className="ap-sw-ghost" />}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <EditProfileModal isOpen={editOpen} onClose={() => setEditOpen(false)} fallbackName="Администратор" />
      <ChangePasswordModal isOpen={pwOpen} onClose={() => setPwOpen(false)} />
      <TrialAccessModal open={trialOpen} onClose={() => setTrialOpen(false)} />
    </div>
  );
};

export default AdminProfilePage;
